// src/modules/components/table-of-contents.js
//
// Inhaltsverzeichnis fuer Blogbeitraege. Webflow liefert nur den Rahmen
// (Seitenleiste, Liste, ein Vorlagen-Link), dieses Modul fuellt ihn aus den
// Ueberschriften des Artikels und fuehrt den aktiven Eintrag beim Scrollen mit.
//
// Drei Dinge halten lange Verzeichnisse benutzbar (Stand 09/2026):
//
// 1. Unterpunkte (h3 und tiefer) liegen in einer Gruppe hinter ihrem h2 und
//    sind nur im gerade gelesenen Abschnitt aufgeklappt. Ein Beitrag mit
//    zwanzig h3 zeigt so meist nur sechs bis acht Zeilen.
// 2. Die Seitenleiste ist per CSS auf die Bildschirmhoehe begrenzt und
//    scrollt intern. Das Modul zieht den aktiven Eintrag dabei in den
//    sichtbaren Bereich nach, sonst wandert er unter die Kante.
// 3. Auf dem Handy ist die Liste hinter einer Schaltflaeche eingeklappt und
//    schliesst sich nach einem Klick auf einen Eintrag wieder.
//
// Die Styles dazu liegen in src/styles/table-of-contents.css.
import { ScrollTrigger } from "@lib/gsap";
import { Scroll } from "@lib/scroll";

const MOBIL = "(max-width: 767px)";
const REDUZIERTE_BEWEGUNG = "(prefers-reduced-motion: reduce)";
// Muss zur Dauer der Aufklapp-Transition in der CSS passen.
const AUFKLAPP_DAUER = 320;

/**
 * Baut die Links in der Liste auf. Eintraege der obersten konfigurierten
 * Ebene stehen direkt in der Liste, alle tieferen wandern in eine Gruppe
 * hinter dem letzten Eintrag der obersten Ebene.
 *
 * Gibt fuer jeden Link den Gruppen-Container zurueck (null fuer oberste
 * Ebene), damit setActive weiss, was aufzuklappen ist. Ohne DOM-Abhaengigkeit
 * ausserhalb von document, deshalb separat testbar.
 */
export function baueEintraege(headings, templateLink, listEl) {
  const tiefen = headings.map((h) => Number(h.tagName.charAt(1)));
  const obersteTiefe = Math.min(...tiefen);

  const links = [];
  const gruppen = [];
  let aktuelleGruppe = null;

  headings.forEach((heading, i) => {
    const clone = templateLink.cloneNode(true);
    const textTarget = clone.querySelector("[data-toc-text]") || clone;
    textTarget.textContent = heading.textContent.trim();

    clone.href = "#" + heading.id;
    clone.removeAttribute("data-toc-link");
    clone.setAttribute("data-toc-item", "");
    clone.setAttribute("data-toc-depth", String(tiefen[i]));
    clone.removeAttribute("data-toc-status");

    if (tiefen[i] === obersteTiefe) {
      listEl.appendChild(clone);
      aktuelleGruppe = null;
    } else {
      if (!aktuelleGruppe) {
        aktuelleGruppe = document.createElement("div");
        aktuelleGruppe.setAttribute("data-toc-group", "");
        const innen = document.createElement("div");
        innen.setAttribute("data-toc-group-inner", "");
        aktuelleGruppe.appendChild(innen);
        listEl.appendChild(aktuelleGruppe);
      }
      aktuelleGruppe.firstElementChild.appendChild(clone);
    }

    links.push(clone);
    gruppen.push(aktuelleGruppe);
  });

  return { links, gruppen };
}

/**
 * Haengt vor die Liste eine Schaltflaeche, die sie auf dem Handy auf- und
 * zuklappt. Auf dem Desktop blendet die CSS die Schaltflaeche aus und die
 * Liste bleibt unabhaengig vom Zustand sichtbar.
 */
export function baueUmschalter(listEl, beschriftung) {
  if (!listEl.id) {
    listEl.id = "toc-liste-" + Math.random().toString(36).slice(2, 8);
  }

  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("data-toc-toggle", "");
  button.setAttribute("aria-controls", listEl.id);
  button.setAttribute("aria-expanded", "false");
  button.innerHTML =
    '<span data-toc-toggle-text></span>' +
    '<svg data-toc-toggle-icon viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">' +
    '<path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    "</svg>";
  button.querySelector("[data-toc-toggle-text]").textContent = beschriftung;

  listEl.setAttribute("data-toc-collapsed", "");
  listEl.parentNode.insertBefore(button, listEl);

  function setze(offen) {
    button.setAttribute("aria-expanded", offen ? "true" : "false");
    if (offen) listEl.removeAttribute("data-toc-collapsed");
    else listEl.setAttribute("data-toc-collapsed", "");
  }

  button.addEventListener("click", () => {
    setze(listEl.hasAttribute("data-toc-collapsed"));
  });

  return { button, setze };
}

export function initTableOfContents() {
  const roots = document.querySelectorAll("[data-toc-wrap]");
  if (!roots.length) return;

  roots.forEach((root) => {
    if (root.dataset.scriptInitialized) return;
    root.dataset.scriptInitialized = "true";

    const contentEl = root.querySelector("[data-toc-content]");
    const listEl = root.querySelector("[data-toc-list]");
    const templateLink = listEl?.querySelector("[data-toc-link]");
    if (!contentEl || !listEl || !templateLink) return;

    const levels = (root.getAttribute("data-toc-levels") || "h2,h3")
      .split(",")
      .map((l) => l.trim().toLowerCase())
      .filter((l) => /^h[1-6]$/.test(l));
    const levelSelector = levels.join(", ");
    if (!levelSelector) return;

    const offset = parseInt(root.getAttribute("data-toc-offset")) || 50;
    const marker = "{skip}";

    const slugCounts = new Map();

    function slugify(text) {
      let slug = text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      if (!slug) slug = "section";

      const count = slugCounts.get(slug) || 0;
      slugCounts.set(slug, count + 1);
      return count === 0 ? slug : slug + "-" + (count + 1);
    }

    function stripMarker(el) {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.includes(marker)) {
          node.textContent = node.textContent.replace(marker, "").trim();
        }
      }
    }

    const allHeadings = [...contentEl.querySelectorAll(levelSelector)];
    const headings = [];

    allHeadings.forEach((heading) => {
      if (heading.hasAttribute("data-toc-ignore")) return;
      if (heading.textContent.includes(marker)) {
        stripMarker(heading);
        return;
      }
      const text = heading.textContent.trim();
      if (!text) return;
      headings.push(heading);
    });

    if (!headings.length) return;

    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = slugify(heading.textContent.trim());
      }
    });

    const { links: tocLinks, gruppen } = baueEintraege(headings, templateLink, listEl);
    listEl.querySelectorAll("[data-toc-link]").forEach((el) => el.remove());

    // Die Beschriftung ("In diesem Beitrag") steht in Webflow direkt vor der
    // Liste. Auf dem Handy uebernimmt die Schaltflaeche ihren Text.
    const labelEl = listEl.previousElementSibling;
    const beschriftung = labelEl?.textContent?.trim() || "Inhalt";
    if (labelEl) labelEl.setAttribute("data-toc-label", "");
    const umschalter = baueUmschalter(listEl, beschriftung);

    const mobil = window.matchMedia(MOBIL);
    const ruhig = window.matchMedia(REDUZIERTE_BEWEGUNG);
    // Das sticky Element ist der Elternknoten der Liste (die Seitenleiste).
    const sidebar = listEl.parentElement;

    // Die Seitenleiste scrollt intern. Lenis faengt sonst jedes Mausrad ab
    // und die Leiste liesse sich nie von Hand bewegen.
    if (sidebar) sidebar.setAttribute("data-lenis-prevent", "");

    // Haelt den aktiven Eintrag im oberen Drittel der Leiste. Gescrollt wird
    // nur, wenn er die Komfortzone verlaesst, sonst zuckt die Leiste bei
    // jedem Abschnittswechsel ein Stueck.
    function aktivenEintragNachziehen(link) {
      if (!sidebar || mobil.matches) return;
      if (sidebar.scrollHeight <= sidebar.clientHeight + 1) return;

      const s = sidebar.getBoundingClientRect();
      const l = link.getBoundingClientRect();
      const relTop = l.top - s.top;
      const zoneOben = s.height * 0.12;
      const zoneUnten = s.height * 0.6;
      if (relTop >= zoneOben && l.bottom - s.top <= zoneUnten) return;

      const ziel = sidebar.scrollTop + relTop - s.height * 0.3;
      sidebar.scrollTo({
        top: Math.max(0, ziel),
        behavior: ruhig.matches ? "auto" : "smooth",
      });
    }

    let nachziehTimer = null;

    // Aktiven Eintrag setzen, dessen Gruppe aufklappen, sichtbar halten
    function setActive(index) {
      const aktiv = tocLinks[index];
      if (!aktiv) return;

      tocLinks.forEach((link) => link.setAttribute("data-toc-status", ""));
      aktiv.setAttribute("data-toc-status", "active");

      // Offen ist die Gruppe, in der der aktive Eintrag liegt, oder die
      // Gruppe direkt hinter einem aktiven Eintrag der obersten Ebene.
      const offen = gruppen[index] || naechsteGruppe(aktiv);
      listEl.querySelectorAll("[data-toc-group]").forEach((g) => {
        if (g === offen) g.setAttribute("data-toc-open", "");
        else g.removeAttribute("data-toc-open");
      });

      // Erst nach dem Aufklappen messen, sonst stimmen die Positionen nicht.
      clearTimeout(nachziehTimer);
      nachziehTimer = setTimeout(
        () => aktivenEintragNachziehen(aktiv),
        ruhig.matches ? 0 : AUFKLAPP_DAUER
      );
    }

    function naechsteGruppe(link) {
      const next = link.nextElementSibling;
      return next && next.hasAttribute("data-toc-group") ? next : null;
    }

    headings.forEach((heading, i) => {
      const nextHeading = headings[i + 1];

      ScrollTrigger.create({
        trigger: heading,
        start: "top " + (offset + 1) + "px",
        endTrigger: nextHeading || contentEl,
        end: nextHeading ? "top " + (offset + 1) + "px" : "bottom top",
        onToggle: (self) => {
          if (self.isActive) setActive(i);
        },
      });
    });

    if (window.scrollY <= headings[0].getBoundingClientRect().top + window.scrollY - offset) {
      setActive(0);
    }

    // Klick mit Smooth Scroll über Lenis
    listEl.addEventListener("click", (e) => {
      const link = e.target.closest("[data-toc-item]");
      if (!link) return;
      e.preventDefault();
      e.stopPropagation();

      const id = link.getAttribute("href")?.slice(1);
      const target = id && document.getElementById(id);
      if (!target) return;

      // Auf dem Handy die Liste wieder schliessen, sie ist nicht sticky und
      // wuerde sonst ueber dem Artikel stehen bleiben.
      if (mobil.matches) umschalter.setze(false);

      if (typeof Scroll?.scrollTo === "function") {
        Scroll.scrollTo(target, { offset: -offset });
      } else {
        const y = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    });
  });

  // Die eingefügten Links verändern die Seitenhöhe – Positionen neu messen
  ScrollTrigger.refresh();
}
